import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import secureStorage from '../lib/secureStorage';
import { toast } from 'sonner';
import { useMessaging } from '../hooks/useMessaging';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const cardTitle = useMessaging('login.card_title', 'Welcome Back');
  const cardSubtitle = useMessaging('login.card_subtitle', 'Sign in to navigate the Missing Middle.');
  const tagline = useMessaging('login.tagline_below_logo', 'Mission Critical Learning');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [demoExchanging, setDemoExchanging] = useState(false);

  // Demo magic-link handoff — if the URL contains ?demo_token=..., swap it
  // for a real session and log the user straight into the Acme tenant.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoToken = params.get('demo_token');
    if (!demoToken) return;
    setDemoExchanging(true);
    (async () => {
      try {
        // Clear any stale session first so we start clean
        secureStorage.removeItem('session_token');
        await api.post('/auth/clear-session').catch(() => {});
        const res = await api.post('/auth/demo-magic-consume', { token: demoToken });
        if (res.data?.session_token) {
          secureStorage.setItem('session_token', res.data.session_token);
        }
        toast.success('Welcome to the Acme Industries demo.', {
          description: 'All data on this tenant is synthetic.',
        });
        // Strip the token from the URL and reload into the authed app state
        window.location.replace('/');
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Demo link is invalid or expired.';
        toast.error(detail);
        // Clean up the URL so the user can sign in manually
        window.history.replaceState({}, '', '/login');
        setDemoExchanging(false);
      }
    })();
  }, []);

  // Clear any stale session on login-page load — but never while a persisted
  // session is still being restored or is valid (a signed-in user landing on
  // /login is redirected below instead of being logged out).
  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    if (new URLSearchParams(window.location.search).get('demo_token')) return;
    secureStorage.removeItem('session_token');
    api.post('/auth/clear-session').catch(() => {});
  }, [authLoading, isAuthenticated]);

  // Redirect away once authenticated.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password, remember);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const AUTH_URL = process.env.REACT_APP_AUTH_URL;
    // Per Emergent Auth playbook: redirect to the main app (dashboard), NOT /login.
    // AppRoutes detects `session_id` in the URL fragment and renders AuthCallback.
    const redirectUrl = window.location.origin + '/';
    window.location.href = `${AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
      {demoExchanging && (
        <div
          className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-slate-900/80 backdrop-blur-sm text-white"
          data-testid="demo-exchanging-overlay"
        >
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-white" />
          <p className="text-sm font-medium">Unlocking the Acme Industries demo…</p>
        </div>
      )}
      <Card className="w-full max-w-md bg-white border border-slate-200 backdrop-blur shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex flex-col items-center gap-1.5 mb-4">
            <img
              src="/seertech_full_lockup.png"
              alt="Seertech · Mission Critical Learning"
              className="h-16 w-auto"
              data-testid="login-brand-lockup"
            />
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              {tagline}
            </p>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800" data-testid="login-card-title">
            {cardTitle}
          </CardTitle>
          <CardDescription data-testid="login-card-subtitle">
            {cardSubtitle}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Google Sign-In */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-2 hover:bg-slate-50"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-slate-500">or sign in with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="remember-me-label">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid="remember-me-checkbox"
                  />
                  Keep me signed in for 30 days
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs font-medium text-teal-600 hover:text-teal-700"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-teal-600 hover:bg-teal-700"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          {/*
            Self-service account creation is intentionally not exposed.
            Account provisioning is restricted to Super Admins and
            Organization Admins via the Admin Dashboard's "Create User"
            flow. Surfacing a public "Create one" link here would
            contradict that policy and confuse end users about who
            controls onboarding.
          */}
          <p
            className="text-center text-xs text-slate-500"
            data-testid="login-no-self-signup-notice"
          >
            New here? Your Super Admin or Organization Admin will provision your account.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
